import React, { useState, useMemo, useCallback } from 'react'
import { Select, MenuItem, FormControl, Collapse, IconButton } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ClearIcon from '@mui/icons-material/Clear'

// Компонент автокомплита для мобильной версии
const MobileAutocomplete = ({ options = [], placeholder, onChange, value, getOptionLabel, inputProps = {}, showClear = true }) => {
  const [inputValue, setInputValue] = useState('')
  const [open, setOpen] = useState(false)
  const [filteredOptions, setFilteredOptions] = useState(options)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
  const blurTimeoutRef = React.useRef(null)
  const containerRef = React.useRef(null)
  const inputRef = React.useRef(null)

  // Мемоизируем текущее значение
  const currentValue = value || null

  // Обновляем inputValue при изменении внешнего значения
  React.useEffect(() => {
    if (currentValue && getOptionLabel) {
      setInputValue(getOptionLabel(currentValue))
    } else if (!currentValue) {
      setInputValue('')
    }
  }, [currentValue, getOptionLabel])

  // Фильтрация опций
  React.useEffect(() => {
    if (!inputValue || (currentValue && getOptionLabel && inputValue === getOptionLabel(currentValue))) {
      setFilteredOptions(options)
    } else {
      const filtered = options.filter(option => 
        option && getOptionLabel && getOptionLabel(option).toLowerCase().includes(inputValue.toLowerCase())
      )
      setFilteredOptions(filtered)
    }
  }, [inputValue, options, getOptionLabel, currentValue])

  // Закрытие при клике вне компонента
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (open && containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false)
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  // Обновляем позицию при изменении размера окна
  React.useEffect(() => {
    const handleResize = () => {
      if (open) {
        updateDropdownPosition()
      }
    }
    
    window.addEventListener('resize', handleResize)
    window.addEventListener('scroll', handleResize)
    
    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('scroll', handleResize)
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current)
      }
    }
  }, [open])

  const handleInputChange = (e) => {
    setInputValue(e.target.value)
    setOpen(true)
  }

  const updateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      })
    }
  }

  const handleInputFocus = () => {
    // Отменяем таймер закрытия, если он есть
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current)
      blurTimeoutRef.current = null
    }
    updateDropdownPosition()
    setOpen(true)
    setFilteredOptions(options)
  }

  const handleOptionSelect = (option) => {
    if (option && getOptionLabel) {
      setInputValue(getOptionLabel(option))
    } else {
      setInputValue('')
    }
    setOpen(false)
    onChange(null, option)
  }

  const handleClear = () => {
    setInputValue('')
    setOpen(true)
    setFilteredOptions(options)
    onChange(null, null)
    inputRef.current?.focus()
  }

  return (
    <div ref={containerRef} className="autocomplete-container" style={{ position: 'relative', width: '100%' }}>
      <input
        ref={inputRef}
        type="text"
        className="form-input"
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={() => {
          blurTimeoutRef.current = setTimeout(() => setOpen(false), 200)
        }}
        style={{ 
          paddingRight: showClear && inputValue ? '35px' : '12px',
          height: '40px',
          fontSize: 'var(--text-sm)'
        }}
        {...inputProps}
      />
      {showClear && inputValue && (
        <IconButton
          onClick={handleClear}
          onMouseDown={(e) => e.preventDefault()}
          size="small"
          style={{
            position: 'absolute',
            right: '4px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--text-muted)',
            width: '24px',
            height: '24px'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'var(--light-gray)'
            e.target.style.color = 'var(--text-light)'
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent'
            e.target.style.color = 'var(--text-muted)'
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </IconButton>
      )}
      {open && filteredOptions.length > 0 && (
        <div style={{
          position: 'fixed',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${dropdownPosition.width}px`,
          backgroundColor: 'var(--medium-gray)',
          border: '1px solid var(--light-gray)',
          borderRadius: 'var(--radius-sm)',
          maxHeight: '40vh',
          overflowY: 'auto',
          zIndex: 9999,
          boxShadow: 'var(--shadow-md)'
        }}>
          {filteredOptions.map((option, index) => (
            <div
              key={index}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleOptionSelect(option)}
              style={{
                padding: '12px 16px',
                cursor: 'pointer',
                borderBottom: index < filteredOptions.length - 1 ? '1px solid var(--light-gray)' : 'none',
                color: 'var(--text-light)',
                fontSize: 'var(--text-sm)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--dark-gray)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              {getOptionLabel(option)}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const MobileFilters = ({
  proxyCountries,
  onCountryChange,
  onIpChange,
  onStateChange,
  onCityChange,
  onZipChange,
  onIspChange,
  onTypeChange,
  onDeviceChange,
  proxies,
  ipInputValue,
  selectedType,
  selectedDev,
  selectedCountry,
  selectedState,
  selectedCity,
  selectedZip,
  selectedIsp
}) => {
  const [expanded, setExpanded] = useState(false)

  // Мемоизируем сегменты данных для лучшей производительности
  const segments = useMemo(() => {
    return {
      regions: proxies?.options?.regions?.sort() || [],
      cities: proxies?.options?.cities?.sort() || [],
      zips: proxies?.options?.zips?.sort() || [],
      isps: proxies?.options?.isps?.sort() || []
    }
  }, [proxies?.options])

  // Мемоизируем обработчики для предотвращения лишних рендеров
  const handleToggleExpanded = useCallback(() => {
    setExpanded(prev => !prev)
  }, [])

  return (
    <div style={{
      background: 'var(--medium-gray)',
      border: '1px solid var(--light-gray)',
      borderRadius: 'var(--radius-lg)',
      marginBottom: 'var(--spacing-sm)',
      overflow: 'hidden'
    }}>
      {/* Заголовок фильтров */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 'var(--spacing-md)',
        cursor: 'pointer'
      }} onClick={handleToggleExpanded}>
        <span style={{
          fontSize: 'var(--text-sm)',
          fontWeight: '600',
          color: 'var(--text-light)'
        }}>
          Filters
        </span>
        <IconButton size="small" style={{ color: 'var(--text-light)' }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </div>

      {/* Содержимое фильтров */}
      <Collapse in={expanded}>
        <div style={{ padding: '0 var(--spacing-md) var(--spacing-md)' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 'var(--spacing-sm)',
            marginBottom: 'var(--spacing-sm)'
          }}>
            {/* Страна */}
            <div>
              <label style={{
                display: 'block',
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px'
              }}>
                Country
              </label>
              <MobileAutocomplete
                value={proxyCountries.find(c => c.id === selectedCountry) || {id: 'US', name: 'United States'}}
                placeholder="Select Country"
                onChange={(event, value) => onCountryChange(value)}
                options={proxyCountries || []}
                getOptionLabel={(option) => option.name}
                inputProps={{
                  autoComplete: "off",
                  autoCorrect: "off",
                  autoCapitalize: "off",
                  spellCheck: "false"
                }}
              />
            </div>

            {/* IP адрес */}
            <div>
              <label style={{
                display: 'block',
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px'
              }}>
                IP Address
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="IP Address"
                value={ipInputValue}
                onChange={(e) => onIpChange(e.target.value)}
                style={{ height: '40px', fontSize: 'var(--text-sm)' }}
              />
            </div>

            {/* Штат/Регион */}
            <div>
              <label style={{
                display: 'block',
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px'
              }}>
                State
              </label>
              <MobileAutocomplete
                value={selectedState ? { id: selectedState, name: selectedState } : null}
                placeholder="All States"
                onChange={(event, value) => onStateChange(value?.id || null)}
                options={segments.regions.map(region => ({ id: region, name: region }))}
                getOptionLabel={(option) => option.name}
                inputProps={{
                  autoComplete: "off",
                  autoCorrect: "off",
                  autoCapitalize: "off",
                  spellCheck: "false"
                }}
              />
            </div>

            {/* Город */}
            <div>
              <label style={{
                display: 'block',
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px'
              }}>
                City
              </label>
              <MobileAutocomplete
                value={selectedCity ? { id: selectedCity, name: selectedCity } : null}
                placeholder="All Cities"
                onChange={(event, value) => onCityChange(value?.id || null)}
                options={segments.cities.map(city => ({ id: city, name: city }))}
                getOptionLabel={(option) => option.name}
                inputProps={{
                  autoComplete: "off",
                  autoCorrect: "off",
                  autoCapitalize: "off",
                  spellCheck: "false"
                }}
              />
            </div>

            {/* ZIP код */}
            <div>
              <label style={{
                display: 'block',
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px'
              }}>
                ZIP
              </label>
              <MobileAutocomplete
                value={selectedZip ? { id: selectedZip, name: selectedZip } : null}
                placeholder="All ZIPs"
                onChange={(event, value) => onZipChange(value?.id || null)}
                options={segments.zips.map(zip => ({ id: zip, name: zip }))}
                getOptionLabel={(option) => option.name}
                inputProps={{
                  autoComplete: "off",
                  autoCorrect: "off",
                  autoCapitalize: "off",
                  spellCheck: "false"
                }}
              />
            </div>

            {/* ISP */}
            <div>
              <label style={{
                display: 'block',
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px'
              }}>
                ISP
              </label>
              <MobileAutocomplete
                value={selectedIsp ? { id: selectedIsp, name: selectedIsp } : null}
                placeholder="All ISPs"
                onChange={(event, value) => onIspChange(value?.id || null)}
                options={segments.isps.map(isp => ({ id: isp, name: isp }))}
                getOptionLabel={(option) => option.name}
                inputProps={{
                  autoComplete: "off",
                  autoCorrect: "off",
                  autoCapitalize: "off",
                  spellCheck: "false"
                }}
              />
            </div>

            {/* Тип подключения */}
            <div>
              <label style={{
                display: 'block',
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px'
              }}>
                Type
              </label>
              <MobileAutocomplete
                value={
                  selectedType === 'wifi'
                    ? { id: 'wifi', name: 'WIFI' }
                    : selectedType === 'cell'
                      ? { id: 'cell', name: 'LTE' }
                      : { id: '', name: 'WIFI/LTE' }
                }
                placeholder="All Types"
                onChange={(event, value) => {
                  onTypeChange(event, value == null ? null : value.id);
                }}
                options={[
                  { id: '', name: 'WIFI/LTE' },
                  { id: 'wifi', name: 'WIFI' },
                  { id: 'cell', name: 'LTE' }
                ]}
                getOptionLabel={(option) => option.name}
                inputProps={{
                  autoComplete: "off",
                  autoCorrect: "off",
                  autoCapitalize: "off",
                  spellCheck: "false"
                }}
                showClear={false}
              />
            </div>

            {/* ОС / устройство */}
            <div>
              <label style={{
                display: 'block',
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px'
              }}>
                OS
              </label>
              <MobileAutocomplete
                value={
                  selectedDev === 'windows'
                    ? { id: 'windows', name: 'Windows' }
                    : selectedDev === 'macos'
                      ? { id: 'macos', name: 'MacOS' }
                      : selectedDev === 'android'
                        ? { id: 'android', name: 'Android' }
                        : { id: '', name: 'All OS' }
                }
                placeholder="All OS"
                onChange={(event, value) => {
                  onDeviceChange(event, value == null ? null : value.id)
                }}
                options={[
                  { id: '', name: 'All OS' },
                  { id: 'windows', name: 'Windows' },
                  { id: 'macos', name: 'MacOS' },
                  { id: 'android', name: 'Android' }
                ]}
                getOptionLabel={(option) => option.name}
                inputProps={{
                  autoComplete: "off",
                  autoCorrect: "off",
                  autoCapitalize: "off",
                  spellCheck: "false"
                }}
                showClear={false}
              />
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  )
}

export default MobileFilters
